import React, { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, Send, Bot } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export function AiAssistantDrawer({ open, onOpenChange }) {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (msg) => {
    const text = (msg ?? input).trim();
    if (!text || loading) return;
    const userMsg = { id: `u-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/ai/chat', {
        message: text,
        history: messages.map(({ role, content }) => ({ role, content })),
        lang,
      });
      setMessages([...newMessages, { id: `a-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      setMessages([...newMessages, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
        role: 'assistant',
        content: '⚠️ ' + (e.response?.data?.detail || e.message || 'Error'),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const quickChips = [
    t('ai_quick_today'),
    t('ai_quick_low_stock'),
    t('ai_quick_classify'),
    t('ai_quick_invoice'),
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="ai-assistant-drawer">
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle className="flex items-center gap-2 font-heading">
            <Sparkles className="h-5 w-5 text-[hsl(var(--primary))]" />
            {t('ai_assistant')}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" ref={scrollRef}>
          {messages.length === 0 && (
            <div className="text-center py-10 px-4">
              <div className="h-14 w-14 rounded-full bg-[hsl(var(--accent))] grid place-items-center mx-auto mb-3">
                <Bot className="h-7 w-7 text-[hsl(var(--primary))]" />
              </div>
              <h3 className="font-heading font-semibold mb-1">{t('ai_assistant')}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {lang === 'es'
                  ? 'Pregunta sobre ventas, inventario, gastos o pide ayuda con PGC y facturas.'
                  : 'Pergunta sobre vendas, estoque, despesas ou peça ajuda com PGC e faturas.'}
              </p>
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={cn(
                'flex',
                m.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={cn(
                  'rounded-2xl px-3 py-2 max-w-[85%] text-sm whitespace-pre-wrap',
                  m.role === 'user'
                    ? 'bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] rounded-br-md'
                    : 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] rounded-bl-md border border-[hsl(var(--border))]'
                )}
                data-testid={m.role === 'assistant' ? 'ai-assistant-bubble' : undefined}
              >
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-3 py-2 bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] text-sm border">
                {t('ai_loading')}
              </div>
            </div>
          )}
        </div>

        <div className="border-t bg-[hsl(var(--card))] px-3 py-2">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {quickChips.map((c) => (
              <button
                key={c}
                onClick={() => send(c)}
                disabled={loading}
                className="text-xs px-2.5 py-1 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50"
                data-testid={`ai-quick-chip-${c.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {c}
              </button>
            ))}
          </div>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('ai_ask')}
              disabled={loading}
              data-testid="ai-assistant-input"
              autoComplete="off"
            />
            <Button type="submit" disabled={loading} data-testid="ai-assistant-send-button" size="icon" className="shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
